
import moment from 'moment';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  reactive,
  onMounted,
} from 'vue';

import PrimeVueMessage from 'primevue/message';

import PrimeVueCalendar from 'primevue/calendar';

import type {
  GenericObject,
  Projects,
  Feeds,
  QualityPresets,
  Discrepancies,
} from '@/types';

import {
  ENTRIES,
  SUBID_STATUS_AUTO_DECLINED,
  SUBID_STATUS_DECLINED,
} from '@/libs/consts';

import useUser from '@/composable/useUser';

import useEntriesNext from '@/composable/useEntries@next';

export default defineComponent({
  components: {
    PrimeVueMessage,
    PrimeVueCalendar,
    Subid: defineAsyncComponent(() => import('@/components/Subid.vue')),
  },
  setup() {
    const { activeUser: user } = useUser();

    const {
      entries: projects,
      entriesLoading: projectsLoading,
      fetchEntries: fetchProjects,
    } = useEntriesNext<Projects>('/api/project/getProjects');

    const {
      entries: feeds,
      entriesLoading: feedsLoading,
      fetchEntries: fetchFeeds,
    } = useEntriesNext<Feeds>('/api/discrepancies/getFeeds');

    const {
      entries: qualityPresets,
      entriesFetched: qualityPresetsFetched,
      fetchEntries: fetchQualityPresets,
    } = useEntriesNext<QualityPresets>('/api/quality-preset/getQualityPresets');

    const {
      entries: discrepancies,
      entriesLoading: discrepanciesLoading,
      entriesFetched: discrepanciesFetched,
      fetchEntries: fetchDiscrepancies,
    } = useEntriesNext<Discrepancies>('/api/discrepancies/getDiscrepancies', ENTRIES);

    const showFilters = ref(false);

    const filtersDateFrom = moment().subtract(7, 'days');

    const filtersDateTo = moment().subtract(1, 'days');

    const filters = reactive<GenericObject>({
      project_id: null,
      feed_id: null,
      min_revenue_est: 0.1,
      date_from: filtersDateFrom.format('YYYY-MM-DD'),
      date_to: filtersDateTo.format('YYYY-MM-DD'),
    });

    const filtersDatePeriod = ref([
      filtersDateFrom.toDate(),
      filtersDateTo.toDate(),
    ]);

    const fetchDiscrepanciesWrapper = async (): Promise<void> => {
      if (!filters.feed_id) {
        return;
      }

      fetchDiscrepancies({
        ...filters,
      });
    };

    const fetchFeedsWrapper = async (): Promise<void> => fetchFeeds({
      project_id: filters.project_id,
    });

    const onProjectChange = (): void => {
      filters.feed_id = null;

      fetchFeedsWrapper();
    };

    const onFiltersDatePeriodChange = (value: Date[]): void => {
      filters.date_from = moment(value[0]).format('YYYY-MM-DD');

      filters.date_to = moment(value[1]).format('YYYY-MM-DD');
    };

    const getStatisticRowClass = (data: GenericObject): string => {
      if (data.subid && (data.subid.status === SUBID_STATUS_AUTO_DECLINED || data.subid.status === SUBID_STATUS_DECLINED)) {
        return 'statistics-table-row-subid-declined';
      }

      return '';
    };

    onMounted(() => Promise.all([
      fetchProjects(),
      fetchFeedsWrapper(),
      fetchQualityPresets(),
      fetchDiscrepanciesWrapper(),
    ]));

    return {
      user,
      projects,
      projectsLoading,
      feeds,
      feedsLoading,
      qualityPresets,
      qualityPresetsFetched,
      discrepancies,
      discrepanciesLoading,
      discrepanciesFetched,
      fetchDiscrepancies,
      showFilters,
      filters,
      filtersDatePeriod,
      fetchDiscrepanciesWrapper,
      onProjectChange,
      onFiltersDatePeriodChange,
      getStatisticRowClass,
    };
  },
});
